'use client';

import { useSession } from '@shared/auth';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useGlobalSheet } from '../../context/GlobalSheetContext';
import { PaymentsSheet } from '../PaymentsSheet/PaymentsSheet';
import { CheckoutFlowOrchestrator } from './CheckoutFlowOrchestrator';
import { useClearCart } from './hooks/useClearCart';

function CheckoutSheet() {
  const { isCheckoutSheetOpen, closeCheckoutSheet, checkoutSheetOptions } = useGlobalSheet();
  const { session } = useSession();
  const t = useTranslations('global_sheets.checkout');
  const [showHeaderTitle, setShowHeaderTitle] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const pathname = usePathname();
  const [previousPathname, setPreviousPathname] = useState(pathname);
  const [hasArrivedAtThankYou, setHasArrivedAtThankYou] = useState(false);
  const [hasLeftAfterPurchase, setHasLeftAfterPurchase] = useState(false);

  if (pathname !== previousPathname) {
    const arrivedAtThankYou = previousPathname !== ROUTES.CHECKOUT.THANK_YOU && pathname === ROUTES.CHECKOUT.THANK_YOU;
    setPreviousPathname(pathname);
    setHasArrivedAtThankYou(arrivedAtThankYou);
    if (isRedirecting && !arrivedAtThankYou) {
      setHasLeftAfterPurchase(true);
    }
  }

  if (isRedirecting && pathname === ROUTES.CHECKOUT.THANK_YOU && !hasArrivedAtThankYou) {
    setHasArrivedAtThankYou(true);
  }

  if ((hasArrivedAtThankYou || hasLeftAfterPurchase) && !isCheckoutSheetOpen) {
    setHasArrivedAtThankYou(false);
    setHasLeftAfterPurchase(false);
    setIsRedirecting(false);
  }

  const playerId = session.profile?.id ?? '';
  const { clearCart } = useClearCart({ playerId });

  useEffect(() => {
    if ((hasArrivedAtThankYou || hasLeftAfterPurchase) && isCheckoutSheetOpen) {
      closeCheckoutSheet();
    }
  }, [hasArrivedAtThankYou, hasLeftAfterPurchase, isCheckoutSheetOpen, closeCheckoutSheet]);

  const handleClose = useCallback(async () => {
    if (isSubmitting || isClosing) {
      return;
    }

    setIsClosing(true);

    try {
      await clearCart();
      closeCheckoutSheet();
    } finally {
      setIsClosing(false);
    }
  }, [isSubmitting, isClosing, clearCart, closeCheckoutSheet]);

  const handleStateChange = useCallback((state: { isSubmitting: boolean; isRedirecting: boolean }) => {
    setIsSubmitting(state.isSubmitting);
    setIsRedirecting(state.isRedirecting);
  }, []);

  if (hasArrivedAtThankYou || hasLeftAfterPurchase || !isCheckoutSheetOpen || !checkoutSheetOptions) {
    return null;
  }

  return (
    <PaymentsSheet
      title={t('title')}
      showBackButton={false}
      showTitle={showHeaderTitle}
      showCloseButton={!isSubmitting}
      open={isCheckoutSheetOpen}
      data-track-feature="Checkout"
      // Skip the fade so the blurred overlay is opaque immediately. The blur
      // still comes from the shared sheet overlay.
      overlayStyle={{ animation: 'none' }}
      onOpenChange={open => {
        if (!open) {
          void handleClose();
        }
      }}
      data-testid="checkout-sheet"
    >
      <CheckoutFlowOrchestrator
        cartSummary={checkoutSheetOptions.cartSummary}
        ticketDetails={checkoutSheetOptions.ticketDetails}
        onSuccess={checkoutSheetOptions.onSuccess}
        ticketMeta={checkoutSheetOptions.ticketMeta}
        productDisplayName={checkoutSheetOptions.productDisplayName}
        onStepChange={setShowHeaderTitle}
        onStateChange={handleStateChange}
      />
    </PaymentsSheet>
  );
}

export { CheckoutSheet };
